import React, { useState, useMemo } from 'react';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
  SortingState,
  ColumnFiltersState,
  VisibilityState,
  RowSelectionState,
} from '@tanstack/react-table';
import {
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Search,
  Filter,
  Download,
  Trash2,
  Eye,
  EyeOff,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/utils';

interface DataTableProps<TData> {
  data: TData[];
  columns: ColumnDef<TData>[];
  searchPlaceholder?: string;
  onSelectionChange?: (selectedRows: TData[]) => void;
  onExport?: (selectedRows: TData[]) => void;
  onDelete?: (selectedRows: TData[]) => void;
  enableSearch?: boolean;
  enableFilters?: boolean;
  enableSelection?: boolean;
  enableExport?: boolean;
  enableColumnVisibility?: boolean;
  pageSize?: number;
  manualPagination?: boolean;
  pageCount?: number;
  totalCount?: number;
  onPaginationChange?: (pagination: { pageIndex: number; pageSize: number }) => void;
  isLoading?: boolean;
}

export function DataTable<TData>({
  data,
  columns,
  manualPagination,
  pageCount,
  totalCount,
  onPaginationChange,
  searchPlaceholder = 'Search...',
  onSelectionChange,
  onExport,
  onDelete,
  enableSearch = true,
  enableFilters = true,
  enableSelection = false,
  enableExport = false,
  enableColumnVisibility = true,
  pageSize = 10,
  isLoading = false,
}: DataTableProps<TData>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [globalFilter, setGlobalFilter] = useState('');
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize,
  });

  // Add selection column if enabled
  const tableColumns = useMemo(() => {
    if (!enableSelection) return columns;

    const selectionColumn: ColumnDef<TData> = {
      id: 'select',
      header: ({ table }) => (
        <input
          type="checkbox"
          className="h-4 w-4 rounded border-gray-300"
          checked={table.getIsAllPageRowsSelected()}
          onChange={(e) => table.toggleAllPageRowsSelected(e.target.checked)}
        />
      ),
      cell: ({ row }) => (
        <input
          type="checkbox"
          className="h-4 w-4 rounded border-gray-300"
          checked={row.getIsSelected()}
          onChange={(e) => row.toggleSelected(e.target.checked)}
        />
      ),
      enableSorting: false,
      enableHiding: false,
    };

    return [selectionColumn, ...columns];
  }, [columns, enableSelection]);

  const table = useReactTable({
    data,
    meta: {
      pagination,
    },
    columns: tableColumns,

    manualPagination: manualPagination,
    manualSorting: manualPagination,
    manualFiltering: manualPagination,
    pageCount: pageCount,

    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: manualPagination ? undefined : getSortedRowModel(),
    getFilteredRowModel: manualPagination ? undefined : getFilteredRowModel(),

    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,

    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      globalFilter,
      pagination,
    },
  });

  React.useEffect(() => {
    if (!manualPagination || !onPaginationChange) return;

    onPaginationChange(pagination);
  }, [pagination]);

  // Handle selection change
  React.useEffect(() => {
    if (onSelectionChange) {
      const selectedRows = table.getFilteredSelectedRowModel().rows.map((row) => row.original);
      onSelectionChange(selectedRows);
    }
  }, [rowSelection, onSelectionChange, table]);

  const selectedRows = table.getFilteredSelectedRowModel().rows.map((row) => row.original);

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          {/* Global Search */}
          {enableSearch && (
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={searchPlaceholder}
                value={globalFilter}
                onChange={(e) => setGlobalFilter(e.target.value)}
                className="pl-8 max-w-sm"
              />
            </div>
          )}

          {/* Column Filters */}
          {enableFilters && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Filter className="mr-2 h-4 w-4" />
                  Filter
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                sideOffset={4}
                collisionPadding={8}
                avoidCollisions
                forceMount
                className="z-50"
              >
                {table
                  .getAllColumns()
                  .filter((column) => column.getCanFilter())
                  .map((column) => {
                    return (
                      <div key={column.id} className="p-2">
                        <Input
                          placeholder={`Filter ${column.id}...`}
                          value={(column.getFilterValue() as string) ?? ''}
                          onChange={(event) => column.setFilterValue(event.target.value)}
                          className="max-w-sm"
                        />
                      </div>
                    );
                  })}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        <div className="flex items-center space-x-2">
          {/* Column Visibility */}
          {enableColumnVisibility && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <EyeOff className="mr-2 h-4 w-4" />
                  View
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {table
                  .getAllColumns()
                  .filter((column) => column.getCanHide())
                  .map((column) => {
                    return (
                      <DropdownMenuCheckboxItem
                        key={column.id}
                        className="capitalize"
                        checked={column.getIsVisible()}
                        onCheckedChange={(value) => column.toggleVisibility(!!value)}
                      >
                        {column.id}
                      </DropdownMenuCheckboxItem>
                    );
                  })}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Export */}
          {enableExport && selectedRows.length > 0 && onExport && (
            <Button variant="outline" size="sm" onClick={() => onExport(selectedRows)}>
              <Download className="mr-2 h-4 w-4" />
              Export ({selectedRows.length})
            </Button>
          )}

          {/* Delete */}
          {enableSelection && selectedRows.length > 0 && onDelete && (
            <Button variant="destructive" size="sm" onClick={() => onDelete(selectedRows)}>
              <Trash2 className="mr-2 h-4 w-4" />
              Delete ({selectedRows.length})
            </Button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl border overflow-x-auto custom-scrollbar w-full">
        <table className="w-full border-collapse min-w-[800px] font-16">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr
                key={headerGroup.id}
                className="
              bg-[#001B6B] text-white font-spectral
                backdrop-blur-md
                sticky top-0 z-10
              "
              >
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="
                      h-12
                      px-3
                      text-center
                      align-middle
                      font-semibold
                      text-white
                      whitespace-nowrap
                      border border-white/30
                    
                    "
                  >
                    {header.isPlaceholder ? null : (
                      <div
                        className={cn(
                          'flex items-center justify-center space-x-2 w-full',
                          header.column.getCanSort() && 'cursor-pointer select-none'
                        )}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        <span>
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </span>
                        {/* {header.column.getCanSort() && (
                          <div className="ml-2 h-4 w-4">
                            {header.column.getIsSorted() === 'desc' ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : header.column.getIsSorted() === 'asc' ? (
                              <ChevronUp className="h-4 w-4" />
                            ) : (
                              <div className="h-4 w-4" />
                            )}
                          </div>
                        )} */}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row, index, rows) => (
                <tr
                  key={row.id}
                  className={cn(
                    'border-b transition-colors',
                    index % 2 === 1 && 'bg-[linear-gradient(to_right,#B7E2FF,#2DAAFD)]',
                    row.getIsSelected() && 'bg-[linear-gradient(to_right,#B7E2FF,#2DAAFD)]'
                  )}
                >
                  {row.getVisibleCells().map((cell) => {
                    const meta = cell.column.columnDef.meta as any;
                    let rowSpan = 1;
                    const cellAlignClass =
                      meta?.align === 'left'
                        ? 'text-left'
                        : meta?.align === 'right'
                          ? 'text-right'
                          : 'text-center';
                    if (meta?.mergeRow) {
                      const value = cell.getValue();
                      const shouldMerge =
                        typeof meta.mergeRow === 'function'
                          ? meta.mergeRow(value, row.original)
                          : true;

                      if (shouldMerge) {
                        if (
                          index > 0 &&
                          rows[index - 1]
                            ?.getVisibleCells()
                            .find((c) => c.column.id === cell.column.id)
                            ?.getValue() === value
                        ) {
                          return null;
                        }
                        for (let i = index + 1; i < rows.length; i++) {
                          if (
                            rows[i]
                              ?.getVisibleCells()
                              .find((c) => c.column.id === cell.column.id)
                              ?.getValue() === value
                          ) {
                            rowSpan++;
                          } else {
                            break;
                          }
                        }
                      }
                    }

                    return (
                      <td
                        key={cell.id}
                        rowSpan={rowSpan > 1 ? rowSpan : undefined}
                        className={cn(
                          'px-3 py-2 align-middle text-foreground whitespace-nowrap border border-black',
                          cellAlignClass,
                          rowSpan > 1 &&
                            index % 2 === 1 &&
                            'bg-[linear-gradient(to_right,#B7E2FF,#2DAAFD)]',
                          rowSpan > 1 && index % 2 === 0 && 'bg-white'
                        )}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    );
                  })}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={table.getAllColumns().length} className="h-24 text-center">
                  {isLoading ? '' : 'No results.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          {enableSelection && (
            <span>
              {table.getFilteredSelectedRowModel().rows.length} of{' '}
              {table.getFilteredRowModel().rows.length} row(s) selected.
            </span>
          )}
        </div>

        <div className="flex items-center space-x-6 lg:space-x-8">
          <div className="flex items-center space-x-2">
            <p className="text-sm font-medium">Rows per page</p>
            <Select
              value={`${pagination.pageSize}`}
              onValueChange={(value) => {
                const newSize = Number(value);

                table.setPageIndex(0);
                table.setPageSize(newSize);
              }}
            >
              <SelectTrigger className="h-8 w-[90px]">
                <SelectValue placeholder={table.getState().pagination.pageSize} />
              </SelectTrigger>
              <SelectContent side="top" position="popper" sideOffset={4} collisionPadding={8}>
                {[5, 10, 20, 50, 100].map((pageSize) => (
                  <SelectItem key={pageSize} value={`${pageSize}`}>
                    {pageSize}
                  </SelectItem>
                ))}
                {totalCount && totalCount > 100 && (
                  <SelectItem value={`${Math.min(totalCount, 999)}`}>
                    All ({totalCount}){totalCount > 999 ? ' — max 999' : ''}
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>

          <div className="flex w-[100px] items-center justify-center text-sm font-medium">
            Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
          </div>

          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              className="hidden h-8 w-8 p-0 lg:flex"
              onClick={() => table.setPageIndex(0)}
              disabled={!table.getCanPreviousPage()}
            >
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="hidden h-8 w-8 p-0 lg:flex"
              onClick={() => table.setPageIndex(table.getPageCount() - 1)}
              disabled={!table.getCanNextPage()}
            >
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
